import {Component, ViewChild} from '@angular/core';
import {KENDO_DROPDOWNS} from '@progress/kendo-angular-dropdowns';
import {TranslocoModule, TranslocoService} from '@jsverse/transloco';

import {BoiteDialogueGeneriqueComponent, BoiteDialogueMessagesComponent, ControleConteneurComponent, SimplePanelComponent} from '../../../../../../val-ammon-composants/val-composants-lib/src/public-api';
import {ValApiResultat} from '../../../../../../val-ammon-services/val-services-communs-lib/src/public-api';
import {EFactVenteService} from '../../../../../../val-ammon-services/val-services-efact-lib/src/lib/val-services-efact-vente.service';
import {MettreAJourActivationPiecesVenteRequest, MettreAJourActivationPiecesVenteResultat} from '../../../../../../val-ammon-services/val-services-efact-lib/src/lib/MettreAJourActivationPiecesVente';

//Clé du titre du traitement — reprise dans la boîte de choix et dans la boîte de restitution
const c_CleTitre = "vente.activer-desactiver.titre";

//Traitement d’activation et de désactivation des pièces de vente (SU #118690)
//  Neutralise des lignes du SAS sans les supprimer. La recherche « Pièces de vente » ne filtre pas
//  EST_DESACTIVE sur l’onglet Défaut : une pièce désactivée reste visible dans la grille, donc
//  réactivable par ce même traitement.
@Component (
{
  selector: 'val-activer-desactiver-pieces-vente',
  imports:
  [
    TranslocoModule,
    KENDO_DROPDOWNS,
    BoiteDialogueGeneriqueComponent,
    BoiteDialogueMessagesComponent,
    SimplePanelComponent,
    ControleConteneurComponent
  ],
  templateUrl: './activer-desactiver-pieces-vente.component.html',
  styleUrl: './activer-desactiver-pieces-vente.component.scss'
})
export class ActiverDesactiverPiecesVenteComponent
{
  //Création
  //  Paramètres
  //    eFactVenteService : Service e-Facture des ventes
  //    translocoService  : Service de traduction
  constructor (private eFactVenteService: EFactVenteService, private translocoService: TranslocoService)
  {
    this.cleTitre = c_CleTitre;
    this.tailleLabels = 90;
    this.nombrePiecesSelectionnees = 0;
    this.actions = [];
    this.actionSelectionnee = null;
  }

  //Boîte de dialogue de choix de l’action
  @ViewChild ("boiteDialogue")
  public boiteDialogue!: BoiteDialogueGeneriqueComponent<void>;

  //Boîte de dialogue de restitution
  @ViewChild ("boiteDialogueMessages")
  public boiteDialogueMessages!: BoiteDialogueMessagesComponent;

  //Clé de traduction du titre du traitement
  public cleTitre: string;

  //Taille des labels
  public tailleLabels: number;

  //Nombre de pièces de la sélection en cours — affiché pour que l’utilisateur sache sur quoi il agit
  public nombrePiecesSelectionnees: number;

  //Actions proposées
  public actions: DonneesActionActivationPieceVente[];

  //Action sélectionnée
  public actionSelectionnee: DonneesActionActivationPieceVente | null;

  //Exécute le traitement
  //  Paramètres
  //    identifiantsPieces : Identifiants des pièces sélectionnées dans la grille
  //  Retourne
  //    Vrai si au moins une pièce a changé d’état — la grille doit alors être rafraîchie
  public async Executer (identifiantsPieces: string[]) : Promise<boolean>
  {
    let action: DonneesActionActivationPieceVente | null;

    if (identifiantsPieces.length === 0)
      return false;

    action = await this.ChoisirAction (identifiantsPieces.length);

    if (action === null)
      return false;

    return this.Appliquer (identifiantsPieces, action);
  }

  //Ouvre la boîte de choix de l’action
  //  Paramètres
  //    nombrePieces : Nombre de pièces de la sélection
  //  Retourne
  //    Action retenue, nulle si l’utilisateur a annulé
  private async ChoisirAction (nombrePieces: number) : Promise<DonneesActionActivationPieceVente | null>
  {
    //Aucune action présélectionnée : la valider par défaut ferait de « Désactiver » un geste
    //accessible par une simple validation, sur des lignes que l’utilisateur n’a pas relues.
    this.actionSelectionnee = null;
    this.nombrePiecesSelectionnees = nombrePieces;

    //Libellés traduits à l’ouverture : la boîte est modale, la langue ne peut pas changer tant qu’elle est affichée
    this.actions =
    [
      {libelle: this.translocoService.translate ("vente.activer-desactiver.action-desactiver"), desactiver: true},
      {libelle: this.translocoService.translate ("vente.activer-desactiver.action-activer"),    desactiver: false}
    ];

    await this.boiteDialogue.Ouvrir ();

    return this.actionSelectionnee;
  }

  //Applique l’action sur la sélection
  //  Paramètres
  //    identifiantsPieces : Identifiants des pièces sélectionnées dans la grille
  //    action             : Action retenue
  //  Retourne
  //    Vrai si au moins une pièce a changé d’état
  private async Appliquer (identifiantsPieces: string[], action: DonneesActionActivationPieceVente) : Promise<boolean>
  {
    let mettreAJourActivationPiecesVenteRequest: MettreAJourActivationPiecesVenteRequest;
    let valApiResultat: ValApiResultat<MettreAJourActivationPiecesVenteResultat>;

    try
    {
      mettreAJourActivationPiecesVenteRequest = new MettreAJourActivationPiecesVenteRequest ();
      mettreAJourActivationPiecesVenteRequest.pieceIds = identifiantsPieces;
      mettreAJourActivationPiecesVenteRequest.desactiver = action.desactiver;

      valApiResultat = await this.eFactVenteService.MettreAJourActivationPieces (mettreAJourActivationPiecesVenteRequest);

      if (valApiResultat.status !== "success")
      {
        await this.Restituer ("exclamation.png",
                              [valApiResultat.message && valApiResultat.message.length > 0
                                 ? valApiResultat.message
                                 : this.translocoService.translate ("vente.activer-desactiver.echec", {titre: this.translocoService.translate (c_CleTitre)})]);

        return false;
      }

      await this.RestituerSucces (action, valApiResultat.resultat ?? new MettreAJourActivationPiecesVenteResultat ());

      return (valApiResultat.resultat?.nombrePiecesModifiees ?? 0) > 0;
    }
    catch (erreur)
    {
      await this.RestituerErreurTechnique (erreur);

      return false;
    }
  }

  //Restitue le résultat d’un traitement réussi
  //  Paramètres
  //    action   : Action retenue
  //    resultat : Résultat du traitement
  private async RestituerSucces (action: DonneesActionActivationPieceVente,
                                 resultat: MettreAJourActivationPiecesVenteResultat) : Promise<void>
  {
    let messages: string[];
    let cleMessage: string;

    cleMessage = action.desactiver ? "vente.activer-desactiver.pieces-desactivees" : "vente.activer-desactiver.pieces-reactivees";

    messages = [this.translocoService.translate (cleMessage, {nb: resultat.nombrePiecesModifiees})];

    //Les pièces déjà dans l’état demandé ne sont pas retouchées : le dire évite de faire chercher
    //une trace de modification qui n’existe pas.
    if (resultat.nombrePiecesInchangees > 0)
      messages.push (this.translocoService.translate ("vente.activer-desactiver.pieces-inchangees", {nb: resultat.nombrePiecesInchangees}));

    //Une pièce hors du périmètre de l’utilisateur est écartée sans faire échouer le lot : sans ce
    //message, l’écart entre la sélection et le nombre traité resterait inexpliqué.
    if (resultat.nombrePiecesNonAutorisees > 0)
      messages.push (this.translocoService.translate ("vente.activer-desactiver.pieces-ecartees", {nb: resultat.nombrePiecesNonAutorisees}));

    await this.Restituer ("information.png", messages);
  }

  //Restitue une erreur technique
  //  Paramètres
  //    erreur : Erreur interceptée
  private async RestituerErreurTechnique (erreur: unknown) : Promise<void>
  {
    let messages: string[];

    messages = [this.translocoService.translate ("vente.activer-desactiver.erreur-technique", {titre: this.translocoService.translate (c_CleTitre)})];

    if (erreur != null && typeof (erreur as {message?: unknown}).message === "string")
      messages.push ((erreur as {message: string}).message);

    await this.Restituer ("exclamation.png", messages);
  }

  //Ouvre la boîte de restitution
  //  Paramètres
  //    icone    : Icone de la boîte
  //    messages : Messages à afficher
  private async Restituer (icone: string, messages: string[]) : Promise<void>
  {
    this.boiteDialogueMessages.titre = this.translocoService.translate (c_CleTitre);
    this.boiteDialogueMessages.icone = icone;
    this.boiteDialogueMessages.messages = messages;

    await this.boiteDialogueMessages.Ouvrir ();
  }

  //Valide le choix de l’action
  public Valider () : void
  {
    this.boiteDialogue.fonctionRetour ();
    this.boiteDialogue.Fermer ();
  }

  //Annule le choix de l’action
  public Annuler () : void
  {
    this.actionSelectionnee = null;

    this.boiteDialogue.fonctionRetour ();
    this.boiteDialogue.Fermer ();
  }
}

//Représente une action d’activation proposée à l’utilisateur
export class DonneesActionActivationPieceVente
{
  //Libellé présenté dans la liste
  public libelle!: string;

  //Vrai si l’action désactive les pièces
  public desactiver!: boolean;
}
